import type { Translate, Trip } from '@itin/shared'
import { daysBetweenIsoDates } from '@itin/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { TimeZonePicker } from '../components/TimeZonePicker.js'
import { Button, Card, ErrorNotice, Field, Input } from '../components/ui.js'
import { api } from '../lib/api.js'
import { formatDate, useLocale, useT } from '../lib/i18n.js'

export function TripsPage() {
  const t = useT()
  const { locale } = useLocale()
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [duplicating, setDuplicating] = useState<Trip | null>(null)
  const [usingTemplate, setUsingTemplate] = useState<Trip | null>(null)
  // Defaulted to where the advisor is sitting, not to a destination. Whatever
  // is prefilled here is what gets used by anyone who does not look, so it
  // should be the one answer that is never silently wrong by five hours.
  const [timezone, setTimezone] = useState(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo',
  )

  const trips = useQuery({ queryKey: ['trips'], queryFn: api.listTrips })
  const templates = useQuery({ queryKey: ['tripTemplates'], queryFn: api.listTripTemplates })

  // Both lists, always: filing a trip as a template takes it out of one and
  // puts it into the other, so refreshing only the one that was clicked leaves
  // the trip on screen twice.
  const invalidateBothLists = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['trips'] }),
      queryClient.invalidateQueries({ queryKey: ['tripTemplates'] }),
    ])

  const setTemplate = useMutation({
    mutationFn: ({ id, isTemplate }: { id: string; isTemplate: boolean }) =>
      api.updateTrip(id, { isTemplate }),
    onSuccess: invalidateBothLists,
  })

  const create = useMutation({
    mutationFn: (input: {
      title: string
      startDate: string
      endDate: string
      timezone: string
    }) => api.createTrip(input),
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey: ['trips'] })
      setShowForm(false)
    },
  })

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    create.mutate({
      title: String(form.get('title')),
      startDate: String(form.get('startDate')),
      endDate: String(form.get('endDate')),
      timezone: String(form.get('timezone')),
    })
  }

  return (
    <div className="flex flex-col gap-6">
      {duplicating && (
        <DuplicateTripCard trip={duplicating} onDone={() => setDuplicating(null)} />
      )}
      {usingTemplate && (
        <UseTemplateCard template={usingTemplate} onDone={() => setUsingTemplate(null)} />
      )}
      {/* Two lists now hold trip titles, so both are addressable: an unscoped
          look for a trip name would find it in whichever one it has moved to. */}
      <Card
        testId="trips-card"
        title={t('trips.title')}
        actions={
          <Button variant="quiet" onClick={() => setShowForm((v) => !v)}>
            {t('trips.new')}
          </Button>
        }
      >
        {trips.isPending && <p className="text-sm text-slate-500">{t('common.loading')}</p>}
        {trips.isError && <ErrorNotice error={trips.error} />}
        {trips.data?.length === 0 && (
          <p className="text-sm text-slate-500">{t('trips.empty')}</p>
        )}

        <ul className="flex flex-col divide-y divide-slate-200">
          {trips.data?.map((trip) => (
            <li key={trip.id} className="flex flex-wrap items-center justify-between gap-2 py-3">
              <Link to={`/trips/${trip.id}`} className="flex flex-col gap-0.5">
                <span className="font-medium">{trip.title}</span>
                <span className="text-sm text-slate-500">
                  {t('trips.dates', {
                    start: formatDate(trip.startDate, locale),
                    end: formatDate(trip.endDate, locale),
                  })}
                  {' · '}
                  {t(`trips.status.${trip.status}`)}
                </span>
              </Link>
              <div className="flex gap-2">
                <Button variant="quiet" onClick={() => setDuplicating(trip)}>
                  {t('trips.duplicate')}
                </Button>
                <Button
                  variant="ghost"
                  disabled={setTemplate.isPending}
                  onClick={() => setTemplate.mutate({ id: trip.id, isTemplate: true })}
                >
                  {t('templates.mark')}
                </Button>
              </div>
            </li>
          ))}
        </ul>
        {setTemplate.isError && <ErrorNotice error={setTemplate.error} />}
      </Card>

      {/*
        Below the trips, not above them. This is the smaller list and the one
        she reaches for at the start of a job, where the list above is the work
        she is in the middle of.
      */}
      <Card testId="templates-card" title={t('templates.title')}>
        <div className="flex flex-col gap-3">
          <p className="text-xs text-slate-500">{t('templates.hint')}</p>
          {templates.isError && <ErrorNotice error={templates.error} />}
          {templates.data?.length === 0 && (
            <p className="text-sm text-slate-500">{t('templates.empty')}</p>
          )}

          <ul className="flex flex-col divide-y divide-slate-200">
            {templates.data?.map((template) => (
              <li
                key={template.id}
                className="flex flex-wrap items-center justify-between gap-2 py-3"
              >
                {/*
                  A length, never the dates. A template's own dates are
                  scaffolding — whatever week she happened to build it in — and
                  printing them invites the reading that the trip you make from
                  it happens then.
                */}
                <Link to={`/trips/${template.id}`} className="flex flex-col gap-0.5">
                  <span className="font-medium">{template.title}</span>
                  <span className="text-sm text-slate-500">{templateLength(t, template)}</span>
                </Link>
                <div className="flex gap-2">
                  <Button variant="quiet" onClick={() => setUsingTemplate(template)}>
                    {t('templates.use')}
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={setTemplate.isPending}
                    onClick={() => setTemplate.mutate({ id: template.id, isTemplate: false })}
                  >
                    {t('templates.unmark')}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </Card>

      {showForm && (
        <Card title={t('trips.new')}>
          <form onSubmit={submit} className="flex flex-col gap-4">
            <Field label={t('trips.field.title')} guidance="trips.field.title.guidance">
              <Input name="title" required maxLength={200} />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('trips.field.startDate')} guidance="trips.field.startDate.guidance">
                <Input name="startDate" type="date" required />
              </Field>
              <Field label={t('trips.field.endDate')} guidance="trips.field.endDate.guidance">
                <Input name="endDate" type="date" required />
              </Field>
            </div>
            <Field label={t('trips.field.timezone')} guidance="trips.field.timezone.guidance">
              <TimeZonePicker name="timezone" value={timezone} onChange={setTimezone} />
            </Field>
            {create.isError && <ErrorNotice error={create.error} />}
            <div className="flex gap-2">
              <Button type="submit" disabled={create.isPending}>
                {t('common.save')}
              </Button>
              <Button type="button" variant="quiet" onClick={() => setShowForm(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          </form>
        </Card>
      )}
    </div>
  )
}

/** How long the template runs. "1 dias" is the kind of thing that reads unfinished. */
function templateLength(t: Translate, template: Trip): string {
  const days = daysBetweenIsoDates(template.startDate, template.endDate) + 1
  return days === 1 ? t('templates.lengthOne') : t('templates.length', { count: days })
}

/**
 * Starting a client's trip from a saved one.
 *
 * Both fields are required, where duplicating leaves both optional. A template
 * has no name worth reusing — "Paris — 5 dias" is how she finds it in the
 * library, not what the traveller should read — and no dates worth keeping:
 * only the spacing between its days survives.
 */
function UseTemplateCard({ template, onDone }: { template: Trip; onDone(): void }) {
  const t = useT()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const createFromTemplate = useMutation({
    mutationFn: (input: { title: string; startDate: string }) =>
      api.createTripFromTemplate({ templateId: template.id, ...input }),
    async onSuccess(created) {
      await queryClient.invalidateQueries({ queryKey: ['trips'] })
      onDone()
      // Straight into the new trip. The template was the starting point; the
      // work is everything she changes from here.
      await navigate(`/trips/${created.id}`)
    },
  })

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    createFromTemplate.mutate({
      title: String(form.get('title')),
      startDate: String(form.get('startDate')),
    })
  }

  return (
    <Card testId="use-template-card" title={t('templates.useTitle', { title: template.title })}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <p className="text-xs text-slate-500">{t('templates.useHint')}</p>
        <Field label={t('trips.field.title')} guidance="templates.use.title.guidance">
          <Input name="title" required maxLength={200} autoFocus />
        </Field>
        <Field label={t('trips.field.startDate')} guidance="templates.use.startDate.guidance">
          <Input name="startDate" type="date" required />
        </Field>
        {createFromTemplate.isError && <ErrorNotice error={createFromTemplate.error} />}
        <div className="flex gap-2">
          <Button type="submit" disabled={createFromTemplate.isPending}>
            {t('templates.create')}
          </Button>
          <Button type="button" variant="quiet" onClick={onDone}>
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    </Card>
  )
}

/**
 * Copying a trip, with the one question worth asking: when.
 *
 * The same itinerary run three months later needs every day and every booking
 * moved, and doing that by hand is the reason nobody reuses a trip. Leaving the
 * date empty keeps the original's, which is right when she is building a variant
 * of a trip that has not happened yet.
 */
function DuplicateTripCard({ trip, onDone }: { trip: Trip; onDone(): void }) {
  const t = useT()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const duplicate = useMutation({
    mutationFn: (input: { title?: string; startDate?: string }) =>
      api.duplicateTrip(trip.id, input),
    async onSuccess(created) {
      await queryClient.invalidateQueries({ queryKey: ['trips'] })
      onDone()
      // Straight into the copy. She pressed duplicate in order to work on it.
      await navigate(`/trips/${created.id}`)
    },
  })

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    const title = String(form.get('title') ?? '').trim()
    const startDate = String(form.get('startDate') ?? '').trim()
    duplicate.mutate({ ...(title ? { title } : {}), ...(startDate ? { startDate } : {}) })
  }

  return (
    <Card testId="duplicate-trip-card" title={t('trips.duplicateTitle', { title: trip.title })}>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <p className="text-xs text-slate-500">{t('trips.duplicateHint')}</p>
        <Field label={t('trips.field.title')} guidance="trips.duplicate.title.guidance">
          <Input name="title" defaultValue={`${trip.title} (2)`} maxLength={200} />
        </Field>
        <Field label={t('trips.field.startDate')} guidance="trips.duplicate.startDate.guidance">
          <Input name="startDate" type="date" />
        </Field>
        {duplicate.isError && <ErrorNotice error={duplicate.error} />}
        <div className="flex gap-2">
          <Button type="submit" disabled={duplicate.isPending}>
            {t('trips.duplicate')}
          </Button>
          <Button type="button" variant="quiet" onClick={onDone}>
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    </Card>
  )
}
